import { Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/v2/accordion";
import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Checkbox } from "@/components/ui/v2/checkbox";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/v2/drawer";
import { Label } from "@/components/ui/v2/label";
import { Separator } from "@/components/ui/v2/separator";

type KnowledgeFilters = {
  syncStatus: string[];
  chunkType: string[];
};

type FilterCounts = {
  syncStatus: Record<string, number>;
  chunkType: Record<string, number>;
  total: number;
};

type KnowledgeFiltersDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: KnowledgeFilters;
  onApply: (filters: KnowledgeFilters) => void;
  searchValue?: string;
  counts?: FilterCounts;
};

const SYNC_STATUS_OPTIONS = [
  { value: "synced", label: "Synced" },
  { value: "pending", label: "Pending" },
  { value: "new", label: "New" },
];

const CHUNK_TYPE_OPTIONS = [
  { value: "domain-knowledge", label: "Domain Knowledge" },
  { value: "q-and-a", label: "Q&A" },
  { value: "example", label: "Example" },
  { value: "procedure", label: "Procedure" },
];

type FilterSectionProps = {
  value: string;
  title: string;
  options: { value: string; label: string }[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  counts?: Record<string, number>;
};

function FilterSection({
  value,
  title,
  options,
  selectedValues,
  onSelectionChange,
  counts,
}: FilterSectionProps) {
  const handleToggle = (optionValue: string) => {
    if (selectedValues.includes(optionValue)) {
      onSelectionChange(selectedValues.filter((v) => v !== optionValue));
    } else {
      onSelectionChange([...selectedValues, optionValue]);
    }
  };

  return (
    <AccordionItem value={value} className="rounded-lg border last:border-b">
      <AccordionTrigger className="px-4">{title}</AccordionTrigger>
      <AccordionContent className="px-4">
        <div className="-mx-2 max-h-64 space-y-2 overflow-y-auto overflow-x-hidden px-2">
          {options.map((option) => (
            <Label
              key={option.value}
              className="hover-tint-primary -mx-2 flex cursor-pointer items-center gap-2 rounded-md p-2 transition-colors"
            >
              <Checkbox
                checked={selectedValues.includes(option.value)}
                onCheckedChange={() => handleToggle(option.value)}
              />
              <span className="flex-1 text-sm">{option.label}</span>
              {counts && (
                <span className="text-xs text-muted-foreground">
                  {counts[option.value] ?? 0}
                </span>
              )}
            </Label>
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export function KnowledgeFiltersDrawer({
  open,
  onOpenChange,
  filters,
  onApply,
  searchValue,
  counts,
}: KnowledgeFiltersDrawerProps) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<KnowledgeFilters>(filters);

  useEffect(() => {
    setCurrent(filters);
  }, [filters, open]);

  const handleApply = useCallback(() => {
    onApply(current);
    onOpenChange(false);
  }, [current, onApply, onOpenChange]);

  const handleClear = useCallback(() => {
    const cleared: KnowledgeFilters = { syncStatus: [], chunkType: [] };
    setCurrent(cleared);
    onApply(cleared);
    onOpenChange(false);
  }, [onApply, onOpenChange]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>
            {t("captain.knowledge.filterTitle")}
            {counts != null && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({counts.total} {counts.total === 1 ? "document" : "documents"})
              </span>
            )}
          </DrawerTitle>
        </DrawerHeader>

        <div className="flex flex-col gap-4 overflow-y-auto px-4">
          {searchValue && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1.5">
                <Search className="h-3 w-3" />
                <span className="text-xs">&quot;{searchValue}&quot;</span>
              </Badge>
            </div>
          )}

          <Separator />

          <Accordion
            type="multiple"
            defaultValue={["syncStatus", "chunkType"]}
            className="flex w-full flex-col gap-4 pb-4"
          >
            <FilterSection
              value="syncStatus"
              title={t("captain.knowledge.filterSyncStatus")}
              options={SYNC_STATUS_OPTIONS}
              selectedValues={current.syncStatus}
              onSelectionChange={(values) =>
                setCurrent((prev) => ({ ...prev, syncStatus: values }))
              }
              counts={counts?.syncStatus}
            />
            <FilterSection
              value="chunkType"
              title={t("captain.knowledge.filterDocumentType")}
              options={CHUNK_TYPE_OPTIONS}
              selectedValues={current.chunkType}
              onSelectionChange={(values) =>
                setCurrent((prev) => ({ ...prev, chunkType: values }))
              }
              counts={counts?.chunkType}
            />
          </Accordion>
        </div>

        <DrawerFooter className="w-full gap-2 md:flex-row">
          <Button onClick={handleApply} className="md:w-1/2">
            {t("captain.knowledge.filterApply")}
          </Button>
          <Button variant="ghost" onClick={handleClear} className="md:w-1/2">
            <Trash2 className="h-4 w-4" />
            {t("captain.knowledge.filterClear")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
